"use client";

import React, { useMemo } from "react";
import { useParams, useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Header } from "@components/header";
import CanvasObject from "src/components/art/CanvasObject";
import { useArtworks, artworkImageUrl } from "src/components/art/useArtwork";
import { artworkAspect } from "src/components/art/artwork-types";

/**
 * One door into wall art, with three ways through it.
 *
 * The customer's thought is "I want something on my wall", and only after that
 * "from my own photo, or a painting, or a photograph". Three separate products
 * in a menu asks them to answer the second question before the first, and
 * hides two thirds of what the shop can do from anyone who only found one.
 *
 * Every route ends in the same canvas, the same sizes and the same cart, which
 * is the point worth making here rather than three times over.
 */
export default function WallArtHubPage() {
  const t = useTranslations("art");
  const locale = useLocale();
  const params = useParams();
  const router = useRouter();
  const localeSegment = (params?.locale as string) ?? locale;

  const { data: artworks } = useArtworks("ART");
  const { data: photographs } = useArtworks("PHOTO");
  const doors = useMemo(() => {
    const featuredArt = artworks?.[0];
    const featuredPhoto = photographs?.[0];

    const list = [
      {
        key: "own",
        title: t("doorOwnTitle"),
        body: t("doorOwnBody"),
        // The id-less entry route resolves the canvas product itself, so this
        // door needs no lookup and is never dead while one is in flight.
        href: `/${localeSegment}/canvas`,
        image: null as string | null,
        aspect: 1.4,
      },
      {
        key: "art",
        title: t("doorArtTitle"),
        body: t("doorArtBody"),
        href: `/${localeSegment}/art`,
        image: featuredArt
          ? artworkImageUrl(featuredArt.slug, "thumb", featuredArt.updatedAt)
          : null,
        aspect: featuredArt ? artworkAspect(featuredArt) : 1.4,
      },
    ];

    // A door to an empty room is worse than no door: only offer photography
    // once there is something behind it.
    if (featuredPhoto) {
      list.push({
        key: "photo",
        title: t("doorPhotoTitle"),
        body: t("doorPhotoBody"),
        href: `/${localeSegment}/photography`,
        image: artworkImageUrl(featuredPhoto.slug, "thumb", featuredPhoto.updatedAt),
        aspect: artworkAspect(featuredPhoto),
      });
    }

    return list;
  }, [artworks, photographs, localeSegment, t]);

  return (
    <>
      <Header sticky={false} />

      <Box sx={{ bgcolor: "background.default", py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: ".14em" }}
          >
            {t("hubKicker")}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, mt: 1, mb: 2, maxWidth: "18ch" }}>
            {t("hubTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: "52ch" }}>
            {t("hubLede")}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Grid container spacing={4}>
          {doors.map((door) => (
            <Grid size={{ xs: 12, sm: doors.length > 2 ? 4 : 6 }} key={door.key}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => router.push(door.href)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    router.push(door.href);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  "&:hover .doorArt": { transform: "translateY(-4px)" },
                }}
              >
                <Box
                  sx={{
                    bgcolor: "background.default",
                    backgroundImage:
                      "linear-gradient(180deg, rgba(17,29,41,.04) 0%, rgba(17,29,41,.10) 100%)",
                    borderRadius: 2,
                    px: 3,
                    py: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 220,
                  }}
                >
                  <Box className="doorArt" sx={{ transition: "transform .22s", width: "72%" }}>
                    {door.image ? (
                      <CanvasObject
                        src={door.image}
                        alt={door.title}
                        aspect={door.aspect}
                      />
                    ) : (
                      // Nothing to show for an upload yet, so show the shape of
                      // what they will get rather than a stock photo of a wall.
                      <Box
                        sx={{
                          aspectRatio: "1.4",
                          border: "2px dashed",
                          borderColor: "divider",
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.secondary",
                          fontSize: 32,
                        }}
                      >
                        +
                      </Box>
                    )}
                  </Box>
                </Box>

                <Stack spacing={0.5} sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {door.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {door.body}
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Said once here rather than three times downstream. */}
        <Stack
          direction="row"
          spacing={3}
          useFlexGap
          flexWrap="wrap"
          justifyContent="center"
          sx={{ mt: 6 }}
        >
          {[t("factWrap"), t("factPrint"), t("delivery")].map((line) => (
            <Typography key={line} variant="body2" color="text.secondary">
              ✓ {line}
            </Typography>
          ))}
        </Stack>
      </Container>
    </>
  );
}
